"use client";

import { getAppVersion } from "@/lib/version";
// Check frequently so users move to the latest build quickly.

import { X } from "lucide-react";
import { useEffect, useState } from "react";

const CHECK_INTERVAL_MS = 5 * 60 * 1000; // Check every 5 minutes

export function VersionUpdater() {
  const [hasUpdate, setHasUpdate] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [initialVersion, setInitialVersion] = useState<string | null>(null);

  useEffect(() => {
    // Set initial version on mount
    const current = getAppVersion();
    setInitialVersion(current);

    const checkVersion = async () => {
      try {
        // Fetch version from server
        // Add timestamp to prevent browser caching of the check itself
        const res = await fetch(`/api/version?t=${Date.now()}`, { cache: "no-store" });
        if (!res.ok) return;

        const data = await res.json();
        const serverVersion = data.version;

        if (initialVersion && serverVersion && serverVersion !== initialVersion) {
          console.log("New version detected:", serverVersion, "Current:", initialVersion);
          setHasUpdate(true);
        }
      } catch (err) {
        // Silent fail for version check
        console.error("Failed to check version:", err);
      }
    };

    // Check immediately and then periodically
    void checkVersion();

    // Check periodically
    const interval = setInterval(checkVersion, CHECK_INTERVAL_MS);

    // Check on window focus
    const onFocus = () => checkVersion();
    window.addEventListener("focus", onFocus);

    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", onFocus);
    };
  }, [initialVersion]);

  if (!hasUpdate || isDismissed) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-blue-600 text-white px-4 py-2 flex items-center justify-between shadow-md">
      <div className="text-sm font-medium flex-1">A new version of ChickyTutor is available!</div>
      <div className="flex items-center gap-3">
        <button
          onClick={() => window.location.reload()}
          className="bg-white text-blue-600 px-3 py-1 rounded text-xs font-bold hover:bg-blue-50 transition-colors uppercase"
        >
          Refresh
        </button>
        <button
          onClick={() => setIsDismissed(true)}
          className="p-1 hover:bg-blue-700 rounded-full transition-colors"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
